import { uid } from "@dhx/ts-common/core";
import { awaitRedraw, el } from "@dhx/ts-common/dom";
import { IEventSystem } from "@dhx/ts-common/events";
import { getBox } from "@dhx/ts-common/html";
import { Filters } from "../filters/Filters";
import locale from "../locales/en";
import { Pivot } from "../Pivot";
import { IConfiguratorConfig, IField, IPivotFields, PivotEvents } from "../types";

// sub fields
export const types = {
	operations: [
		{ id: "sum", label: "sum" },
		{ id: "min", label: "min" },
		{ id: "max", label: "max" },
		{ id: "count", label: "count" },
	],
	dates: [
		{ id: "dateByDay", label: "day" },
		{ id: "dateByWeek", label: "week" },
		{ id: "dateByMonth", label: "month" },
		{ id: "dateByQuarter", label: "quarter" },
		{ id: "dateByYear", label: "year" },
	],
};
const defaultFields = {
	free: {
		label: "availableFields",
	},
	columns: {
		label: "columns",
		helper: "moveFieldsHere",
	},
	rows: {
		label: "rows",
		helper: "moveFieldsHere",
	},
	values: {
		label: "values",
		helper: "moveFieldsHere",
	},
};

export class Configurator {
	public config: IConfiguratorConfig;
	public events: IEventSystem<PivotEvents>;

	private _pivot: Pivot;
	private _pressedItem: any;
	private _pressedCoords: any;
	private _isDragStart: boolean;
	private _dropArea: string;
	private _changeItem: IField;
	private _htmlEvents: any;
	private _filters: Filters;

	constructor(pivot, config: IConfiguratorConfig) {
		this.config = config;
		this._pivot = pivot;
		this._htmlEvents = {
			fieldOver: (name: string) => {
				if (this._isDragStart) {
					this._dropArea = name;
				}
			},
			itemOver: (item: IField) => {
				if (this._isDragStart) {
					this._dropArea = item.$field;
					this._changeItem = item;
				}
			},
			fieldOut: () => {
				if (this._isDragStart) {
					this._dropArea = null;
				}
			},
			itemOut: () => {
				if (this._isDragStart) {
					this._changeItem = null;
				}
			},
			change: (item: IField, type: { id: string; name: string }, e: any) => {
				item[type.name] = e.target.value;
				this.config.events.fire(PivotEvents.change);
			},
		};
		this.events = this.config.events;
		this._filters = new Filters(this.config.events);

		this.events.on(PivotEvents.fieldClick, (_ev, id, type) => {
			const item = this._getFieldObj(id);
			switch (type) {
				case "selector":
					if (this.config.layout.fieldSelectorType === "loop") {
						this._toggleSelector(item);
						this.config.events.fire(PivotEvents.change);
					}
					break;
				case "filter":
					if (this.config.layout.showFilters !== false) {
						this._pressedItem = item;
						this._filters.getFilter(
							this._pressedItem,
							this._pressedCoords.target,
							this._pivot.config.data,
							this._pivot.getFilterValue(this._pressedItem.id)
						);
					}
					break;
				case "close":
					this._dropArea = "free";
					this._pressedItem = item;
					this._updateFields();

					this.config.events.fire(PivotEvents.change);
					break;
				default:
					break;
			}
		});
	}

	getField(name: string) {
		const config = this._pivot.config;
		const field = defaultFields[name];

		const onHover = this._isDragStart && this._pressedItem.$field !== name && "dhx_on_hover";

		return el(
			".dhx_config_field",
			{
				dhx_id: name,
				onmouseover: [this._htmlEvents.fieldOver, name],
				onmouseout: [this._htmlEvents.fieldOut, name],
				class: onHover,
			},
			[
				el(".dhx_field_header", [
					el(".field_name", locale[field.label]),
					field.helper && el(".field_helper", locale[field.helper]),
				]),
				el(
					".dhx_field_content",
					config.fields[name].map(f => {
						return this._getItem(f);
					})
				),
			]
		);
	}

	setFields(fields: IPivotFields) {
		this.config.fields = fields;
	}

	getFields(): IPivotFields {
		const { rows, columns, values } = this.config.fields;

		return {
			rows,
			columns,
			values,
		};
	}

	getShadow() {
		const item = this._pressedItem;
		if (!item || !this._isDragStart) {
			return el("div");
		}
		const target = getBox(this._pressedCoords.target);
		const shiftX = this._pressedCoords.nextX || target.left;
		const shiftY = this._pressedCoords.nextY || target.top;
		const filters = this._pivot.getFiltersValues();
		return el(
			"div",
			{
				class: "dhx_shadow",
				style: {
					top: shiftY,
					left: shiftX,
					pointerEvents: "none",
				},
			},
			[
				el(".item_body", [
					this.config.layout.showFilters !== false &&
						el(".dhx_item_filter", {
							class: `dxi dxi-filter-variant ${
								filters[item.id] ? "dhx_item_filter--flat" : ""
							}`,
						}),
					el(".item_content", item.label || item.id),
				]),
				this._getSelector(item),
			]
		);
	}

	setPressedItem(ev: any, id: string, node: HTMLElement) {
		this._pressedItem = this._getFieldObj(id);
		this._pressedCoords = {
			x: ev.pageX || ev.changedTouches[0].pageX,
			y: ev.pageY || ev.changedTouches[0].pageY,
			target: node,
		};

		document.body.classList.add("dhx_noselect");
		document.addEventListener("mousemove", this._handleMouseMove);
		document.addEventListener("touchmove", this._handleMouseMove, { passive: false });
		document.addEventListener("mouseup", this._handleMouseup);
		document.addEventListener("touchend", this._handleMouseup);
	}

	private _getDropArea(el: HTMLElement) {
		if (!el.offsetParent) return;
		if (
			el.offsetParent.getAttribute("dhx_id") &&
			el.offsetParent.className.includes("dhx_config_field")
		) {
			this._dropArea = el.offsetParent.getAttribute("dhx_id");
			return;
		}
		this._getDropArea(el.offsetParent as HTMLElement);
	}

	private _getFieldObj(id: string): IField {
		const config = this._pivot.config;

		for (const key in config.fields) {
			for (const obj of config.fields[key]) {
				if (obj.$uid === id) {
					return obj;
				}
			}
		}
	}

	private _handleMouseMove = (e: any) => {
		e.preventDefault();
		const pageX = e.pageX || e.changedTouches[0].pageX;
		const pageY = e.pageY || e.changedTouches[0].pageY;
		if (Math.abs(pageX - this._pressedCoords.x + pageY - this._pressedCoords.y) < 10) {
			return;
		}
		this._isDragStart = true;
		const target = getBox(this._pressedCoords.target);
		const clientX = e.clientX || e.changedTouches[0].clientX;
		const clientY = e.clientY || e.changedTouches[0].clientY;
		const x = clientX - (this._pressedCoords.x - target.left);
		const y = clientY - (this._pressedCoords.y - target.top);
		this._pressedCoords.nextX = x;
		this._pressedCoords.nextY = y;
		this._pivot.paint();
	};

	private _handleMouseup = (_e: any) => {
		if (_e.changedTouches) {
			this._getDropArea(
				document.elementFromPoint(
					_e.changedTouches[0].clientX,
					_e.changedTouches[0].clientY
				) as HTMLElement
			);
		}
		document.removeEventListener("mousemove", this._handleMouseMove);
		document.removeEventListener("touchmove", this._handleMouseMove);
		document.removeEventListener("mouseup", this._handleMouseup);
		document.removeEventListener("touchend", this._handleMouseup);
		document.body.classList.remove("dhx_noselect");
		const drag = this._isDragStart;
		this._updateFields();
		this._isDragStart = false;
		this._pressedItem = null;
		this._dropArea = null;
		this._changeItem = null;
		this._pivot.paint();
		if (this.config.layout.liveReload !== false && drag) {
			awaitRedraw().then(() => {
				this.events.fire(PivotEvents.change);
			});
		}
	};

	private _getItem(item: IField) {
		const onHover =
			this._isDragStart && this._changeItem && this._changeItem.$uid === item.$uid && "dhx_on_hover";
		const filters = this._pivot.getFiltersValues();
		return el(
			".dhx_config_item",
			{
				class: onHover,
				dhx_id: item.$uid,
				onmouseover: [this._htmlEvents.itemOver, item],
				onmouseout: [this._htmlEvents.itemOut],
			},
			[
				el(
					".item_body",
					{
						_ref: item.$uid,
						class: !(this._pivot as any).grid.data.getLength() ? "item_body--disabled" : "",
					},
					[
						this.config.layout.fieldsCloseBtn === true &&
							item.$field !== "free" &&
							el(".dhx_close", { class: "dxi dxi-close" }),
						this.config.layout.showFilters !== false &&
							el(".dhx_item_filter", {
								class: `dxi dxi-filter-variant ${
									filters[item.id] ? "dhx_item_filter--flat" : ""
								}`,
							}),
						el(".item_content", item.label || item.id),
					]
				),
				this._getSelector(item),
			]
		);
	}

	private _moveItem(areaId: string, targetItem: string, item: string): void {
		const config = this._pivot.config;
		const ids = config.fields[areaId].map(field => field.$uid);

		if (ids.indexOf(item) === -1) {
			config.fields[areaId].splice(ids.indexOf(targetItem), 0, this._pressedItem);
			return;
		}
		config.fields[areaId].splice(
			ids.indexOf(targetItem),
			0,
			config.fields[areaId].splice(ids.indexOf(item), 1)[0]
		);
	}

	private _deleteItem(id: string) {
		const config = this._pivot.config;
		const item = this._getFieldObj(id);
		config.fields[item.$field] = config.fields[item.$field].filter(f => f.$uid !== item.$uid);
	}

	private _updateFields() {
		const config = this._pivot.config;
		if (this._pressedItem && this._dropArea) {
			if (this._pressedItem.$field === this._dropArea) {
				if (!this._changeItem) {
					return;
				}
			}
			if (this._dropArea === "values") {
				if (this._pressedItem.$field === this._dropArea) {
					if (this._changeItem) {
						this._moveItem(this._dropArea, this._changeItem.$uid, this._pressedItem.$uid);
					}
					return;
				}
				const copy = { ...this._pressedItem };

				copy.$field = this._dropArea;
				copy.$temp = true;
				copy.$uid = uid();
				this._toggleSelector(copy);
				config.fields[this._dropArea].push(copy);
			} else {
				if (this._pressedItem.$temp) {
					this._deleteItem(this._pressedItem.$uid);
				} else {
					if (this._pressedItem.$field !== this._dropArea) {
						this._deleteItem(this._pressedItem.$uid);
					}

					const fieldItems = config.fields[this._dropArea];
					this._pressedItem.$field = this._dropArea;

					if (this._changeItem) {
						this._moveItem(this._dropArea, this._changeItem.$uid, this._pressedItem.$uid);
					} else {
						fieldItems.push(this._pressedItem);
					}
				}
			}
		}
	}

	private _getSelectorType(item: IField): { id: "dates" | "operations"; name: "group" | "method" } {
		let id;
		let name;
		if (types[item.id]) {
			id = item.id;
			name = "group";
		}
		if (item.type === "date") {
			id = "dates";
			name = "group";
		}

		if (item.$field === "values") {
			id = "operations";
			name = "method";
		}
		if (!id) {
			return;
		}
		return {
			id,
			name,
		};
	}

	private _getSelector(item: IField) {
		const type = this.config.layout.fieldSelectorType;

		switch (type) {
			case "loop":
				return this._getLoopSelect(item);
			case "dropdown":
				return this._getDropdownSelect(item);
			default:
				break;
		}
	}

	private _getLoopSelect(item: IField) {
		const type = this._getSelectorType(item);
		if (!type) {
			return;
		}
		const val = item[type.name]
			? types[type.id].filter(t => t.id === item[type.name])[0]
			: types[type.id][0];
		if (!val) {
			return null;
		}
		item[type.name] = val.id;
		const text = locale[val.label] || val.label;
		return el(".dhx_config_select", { dhx_date: item.id }, [
			el("span", { class: "select_text" }, text),
			el("i", { class: "dxi dxi-unfold-more-horizontal" }),
		]);
	}

	private _getDropdownSelect(item: IField) {
		const type = this._getSelectorType(item);
		if (!type) {
			return;
		}
		const methods = types[type.id];

		return el(".dhx_item-modes", [
			el(
				"select.dhx_item-modes__select",
				{
					_key: item.$uid,
					onchange: [this._htmlEvents.change, item, type],
				},
				methods.map(method => {
					const localeLabel =
						locale.types[type.id] && locale.types[type.id].find(l => l.id === method.id)
							? locale.types[type.id].find(l => l.id === method.id).label
							: method.label;
					return el(
						"option.dhx_select__option",
						{
							value: method.id,
							selected: item[type.name] === method.id,
						},
						localeLabel
					);
				})
			),
			el(".dxi.dxi-chevron-down.dhx_select__icon"),
		]);
	}

	private _toggleSelector(item: IField) {
		const type = this._getSelectorType(item);
		if (!type) {
			return;
		}
		const config = types[type.id];

		let next = config[0];
		for (let i = 0; i < config.length; i++) {
			if (config[i].id === item[type.name]) {
				if (!config[i + 1]) {
					next = config[0];
					break;
				}
				next = config[i + 1];
			}
		}
		item[type.name] = next.id;
	}
}
